/**
 * Office Add-in Action Registry
 *
 * Centralizes all Office.actions.associate calls for ribbon buttons and keyboard shortcuts.
 * Actions are defined declaratively and registered automatically.
 */

interface ActionConfig {
  /** Action ID - must match the FunctionName in manifest and id in shortcuts.json */
  id: string;
  /** Handler function executed when action is triggered */
  handler: () => Promise<void> | void;
  /** Whether to show the taskpane before executing handler (default: true) */
  showTaskpane?: boolean;
}

/** Shared key between the action handler and MainLayout's cold-start drain. */
export const PENDING_NAVIGATION_KEY = "wfi:pendingNavigation";

/**
 * Dispatches a navigation event to the React app, plus stashes the target
 * path in sessionStorage as a cold-start fallback.
 *
 * On the first ribbon click the taskpane is still opening — `showAsTaskpane()`
 * returns before React has mounted, so MainLayout's `navigateTo` listener
 * isn't attached yet when the event fires. Without the stash the event goes
 * into the void, users see the pane open on the default route, and the
 * second click is the one that actually navigates. With the stash,
 * MainLayout drains sessionStorage on mount and navigates for us.
 *
 * Hot-path (pane already open): the listener processes the event and
 * clears the stash before the cold-start drain can pick it up.
 */
function dispatchNavigateEvent(path: string): void {
  sessionStorage.setItem(PENDING_NAVIGATION_KEY, path);
  window.dispatchEvent(new CustomEvent("navigateTo", { detail: path }));
}

/**
 * Action definitions
 */
const actions: ActionConfig[] = [
  // Taskpane visibility
  {
    id: "ShowTaskpane",
    handler: () => {},
    showTaskpane: true,
  },
  {
    id: "HideTaskpane",
    handler: () => {
      return Office.addin.hide();
    },
    showTaskpane: false,
  },

  // Navigation actions
  {
    id: "GoToDashboard",
    handler: () => dispatchNavigateEvent("/"),
  },
  {
    id: "GoToDiscussions",
    handler: () => dispatchNavigateEvent("/discussions"),
  },
  {
    id: "GoToWorkflowStatus",
    handler: () => dispatchNavigateEvent("/workflow-status"),
  },

  // Workflow actions
  {
    id: "CreateReviewPoint",
    handler: () => dispatchNavigateEvent("/discussions?create=true&type=review_point"),
  },
  {
    id: "MarkSheetReviewed",
    handler: () => {
      // Fire-and-toast: skip the confirmation dialog and immediately sign off
      // the active sheet. MainLayout listens for this event, performs the
      // sign-off against the current sheet's status items, and surfaces a
      // toast with the result. The full multi-sheet dialog still lives on the
      // Workflow Status page for users who want to pick more than one sheet.
      window.dispatchEvent(new CustomEvent("signOffCurrentSheet"));
    },
  },
];

/**
 * Registers all actions with Office.actions.associate
 * Call this at module load time (not inside Office.onReady)
 */
export function registerActions(): void {
  console.log("Registering Office actions...");
  try {
    actions.forEach(({ id, handler, showTaskpane = true }) => {
      console.log(`Registering action: ${id}`);
      Office.actions.associate(id, (event) => {
        const execute = async () => {
          try {
            if (showTaskpane) {
              await Office.addin.showAsTaskpane();
            }
            return await handler();
          } catch (error: unknown) {
            const errorCode = error && typeof error === "object" && "code" in error ? (error as { code: string }).code : "unknown";
            console.error(`Action ${id} failed:`, error);
            return errorCode;
          }
        };

        return execute().finally(() => {
          event.completed();
        });
      });
    });
    console.log("All Office actions registered successfully");
  } catch (error) {
    console.error("Failed to register Office actions:", error);
  }
}
